import { quoteQuery } from "@common/api/quoting/quote"
import { useDeleteQuoteTaskStepMutation } from "@common/api/quoting/quote-task-step"
import { currentUserQuery } from "@common/api/users"
import type { QuoteTaskStepPublic } from "@common/client"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { hasPermission } from "@common/lib/permissions"
import { plural } from "@common/lib/sentences"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"

export const Route = createFileRoute(
  "/_layout/quotes_/$quoteId/tasks/$taskId/steps/$stepId/remove",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "quote:update")) {
      throw redirect({ to: "/quotes/$quoteId/tasks/$taskId", params, search })
    }
  },
  component: RemoveStepDialog,
})

function substepCount(steps: QuoteTaskStepPublic[], stepId: string): number {
  return steps
    .filter((step) => step.parent_step_id === stepId)
    .reduce((total, step) => total + 1 + substepCount(steps, step.id), 0)
}

function RemoveStepDialog(): React.ReactElement {
  const { quoteId, taskId, stepId } = Route.useParams()
  const { data: quote } = useSuspenseQuery(quoteQuery({ quote_id: quoteId }))
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useDeleteQuoteTaskStepMutation()
  const steps =
    quote.active_tasks.find((task) => task.id === taskId)?.active_steps ?? []
  const step = steps.find((candidate) => candidate.id === stepId)
  const substeps = substepCount(steps, stepId)
  const removed = substeps > 0 ? ` and its ${plural(substeps, "sub-step")}` : ""
  const description = step
    ? `“${step.description}”${removed} will be removed from the task.`
    : "This step will be removed from the task."

  const close = () =>
    navigate({
      to: "/quotes/$quoteId/tasks/$taskId",
      params: { quoteId, taskId },
      search: true,
    })

  const confirm = () =>
    mutation.mutate(
      { quote_id: quoteId, task_id: taskId, step_id: stepId },
      {
        onSuccess: () => {
          showToast("Removed", "Step removed.", "success")
          close()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title="Remove this step?"
      description={description}
      action="Remove"
      tone="default"
      pendingAction="Removing…"
      pending={mutation.isPending}
      onConfirm={confirm}
      onCancel={close}
    />
  )
}
